import { create } from "zustand";
import { isWorkspaceDevAuth } from "../lib/workspaceDevAuth";
import {
  login as apiLogin,
  devLogin as apiDevLogin,
  logout as apiLogout,
  exchangeClerkSession as apiExchangeClerkSession,
  getMe,
  impersonateUser,
  stopImpersonation as apiStopImpersonation,
  type LoginResponse,
} from "../lib/api";
import { useAppStore } from "./useAppStore";
import { queryClient } from "../main";
import { useLocaleStore } from "./useLocale";
import { clearSnapshotPins } from "../lib/snapshotPins";
import {
  clearClerkAuthSuppressions,
  hasClerkExchangeFailed,
  hasExplicitLogoutRequest,
  markClerkExchangeFailed,
  markExplicitLogoutRequest,
} from "../lib/clerkPortal";
import {
  getClerkSessionClient,
  setExchangedClerkIdentity,
  signOutClerkSession,
} from "../lib/clerkSession";

let bootstrapInFlight: Promise<void> | null = null;
let clerkExchangeInFlight: {
  identity: string;
  operation: number;
  promise: Promise<LoginResponse>;
} | null = null;
let authOperationGeneration = 0;
const pendingSessionEstablishments = new Set<Promise<unknown>>();
const AUTH_ME_MAX_ATTEMPTS = 3;
const AUTH_ME_RETRY_DELAYS_MS = [150, 300];

class AuthMeVerificationError extends Error {
  constructor(readonly originalError: unknown) {
    super("The authenticated session could not be verified.");
    this.name = "AuthMeVerificationError";
  }
}

function trackSessionEstablishment<T>(request: Promise<T>): Promise<T> {
  let tracked!: Promise<T>;
  tracked = request.finally(() => {
    pendingSessionEstablishments.delete(tracked);
  });
  pendingSessionEstablishments.add(tracked);
  return tracked;
}

async function waitForPendingSessionEstablishments(): Promise<void> {
  while (pendingSessionEstablishments.size > 0) {
    await Promise.allSettled([...pendingSessionEstablishments]);
  }
}

class SupersededAuthOperation extends Error {}
function isClerkEnabled() {
  return !isWorkspaceDevAuth() && Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.trim());
}

function resetSessionScopedData(clearDemoLanguage = true, preserveSelection = false) {
  queryClient.clear();
  clearSnapshotPins();
  if (!preserveSelection) useAppStore.getState().setHerd(null);
  if (clearDemoLanguage) useLocaleStore.getState().clearDemoLanguage();
}

function authErrorMessage(err: unknown, fallback: string): string {
  const source = err instanceof AuthMeVerificationError ? err.originalError : err;
  const response = (source as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (!response || response.status === 429 || (response.status ?? 0) >= 500) {
    return "Server vorübergehend nicht erreichbar. Bitte kurz warten und erneut versuchen.";
  }
  const detail = response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return (detail[0] as { msg?: string })?.msg ?? fallback;
  }
  return fallback;
}

function authErrorSource(err: unknown): unknown {
  return err instanceof AuthMeVerificationError ? err.originalError : err;
}

function isRetryableAuthMeError(err: unknown): boolean {
  const source = authErrorSource(err);
  const error = source as { code?: string; response?: { status?: number } };
  if (error?.code === "ERR_CANCELED") return false;
  const status = error?.response?.status;
  return !status || [429, 502, 503, 504].includes(status);
}

async function getConfirmedMe(): Promise<LoginResponse> {
  for (let attempt = 0; attempt < AUTH_ME_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await getMe();
    } catch (err: unknown) {
      if (!isRetryableAuthMeError(err) || attempt === AUTH_ME_MAX_ATTEMPTS - 1) {
        throw new AuthMeVerificationError(err);
      }
      await new Promise((resolve) => setTimeout(resolve, AUTH_ME_RETRY_DELAYS_MS[attempt]));
    }
  }
  throw new Error("Unreachable /auth/me retry state.");
}

function isRetryableAuthTransportError(err: unknown): boolean {
  if (err instanceof AuthMeVerificationError) return isRetryableAuthMeError(err);
  const status = (err as { response?: { status?: number } })?.response?.status;
  return !status || status === 429 || status >= 500;
}

function exchangeClerkIdentity(identity: string, operation: number): Promise<LoginResponse> {
  if (clerkExchangeInFlight) {
    const active = clerkExchangeInFlight;
    if (active.identity === identity && active.operation === operation) return active.promise;
    // An identity switch waits for the old exchange to finish, then verifies
    // that it is still the latest requested identity before starting another.
    return active.promise.catch(() => undefined).then(() => {
      if (operation !== authOperationGeneration) throw new SupersededAuthOperation();
      return exchangeClerkIdentity(identity, operation);
    });
  }
  const client = getClerkSessionClient();
  if (!client?.isLoaded || !client.isSignedIn || !identity || client.userId !== identity) {
    return Promise.reject(new Error("Keine geladene Alta-Clerk-Sitzung verfügbar."));
  }
  const exchange = (async () => {
    const sessionToken = await client.getToken();
    const latestClient = getClerkSessionClient();
    if (
      operation !== authOperationGeneration
      || !latestClient?.isLoaded
      || !latestClient.isSignedIn
      || latestClient.userId !== identity
    ) {
      throw new SupersededAuthOperation();
    }
    if (!sessionToken) throw new Error("Clerk konnte kein Sitzungstoken bereitstellen.");
    await trackSessionEstablishment(apiExchangeClerkSession(sessionToken));
    const exchangedClient = getClerkSessionClient();
    if (
      operation !== authOperationGeneration
      || !exchangedClient?.isLoaded
      || !exchangedClient.isSignedIn
      || exchangedClient.userId !== identity
    ) {
      throw new SupersededAuthOperation();
    }
    const user = await getConfirmedMe();
    if (user.must_change_password) {
      // A successful POST alone is not proof of an SSO cookie. Do not accept
      // a restricted /me result or repeatedly try to upgrade that session.
      throw Object.assign(new Error("Portal session was not confirmed."), {
        response: {
          status: 403,
          data: { detail: "Die Portal-Anmeldung konnte nicht als DairyInsight-Sitzung bestätigt werden. Bitte erneut anmelden." },
        },
      });
    }
    const verifiedClient = getClerkSessionClient();
    if (
      operation !== authOperationGeneration
      || !verifiedClient?.isLoaded
      || !verifiedClient.isSignedIn
      || verifiedClient.userId !== identity
    ) {
      throw new SupersededAuthOperation();
    }
    return user;
  })();
  const promise = exchange.finally(() => {
    if (clerkExchangeInFlight?.promise === promise) clerkExchangeInFlight = null;
  });
  clerkExchangeInFlight = { identity, operation, promise };
  return promise;
}

interface AuthState {
  user: LoginResponse | null;
  token: string | null;
  isLoading: boolean;
  /** True once the initial /auth/me cookie check has completed. */
  sessionChecked: boolean;
  error: string | null;
  /** True when bootstrap failed because the backend was unreachable (network error). */
  networkError: boolean;
  /** Monotonic browser-session generation used to isolate page-local stores. */
  sessionVersion: number;
  /** Current Clerk identity when this cookie session was created by exchange. */
  clerkIdentity: string | null;
  /** A Clerk exchange or its read-only session verification remains retryable. */
  clerkRetryPending: boolean;
  /** The Clerk cookie was exchanged, but its read-only /auth/me verification needs retrying. */
  clerkVerificationPending: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginDev: (key: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  refreshClerkIdentity: (identity: string) => Promise<void>;
  retryClerkLogin: () => Promise<void>;
  clearNetworkError: () => void;
  clearMustChangePassword: () => void;
  /** Admin impersonation: switch this session to the target user's view. */
  impersonate: (userId: number) => Promise<void>;
  /** End impersonation and restore the original admin session. */
  stopImpersonation: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  // This is only an in-memory session marker. The JWT itself is HttpOnly and
  // never available to JavaScript.
  token: "cookie-session",
  isLoading: false,
  sessionChecked: false,
  error: null,
  networkError: false,
  sessionVersion: 0,
  clerkIdentity: null,
  clerkRetryPending: false,
  clerkVerificationPending: false,

  login: async (username, password) => {
    const operation = ++authOperationGeneration;
    clearClerkAuthSuppressions();
    setExchangedClerkIdentity(null);
    set({
      user: null,
      token: null,
      isLoading: true,
      error: null,
      networkError: false,
      clerkIdentity: null,
      clerkRetryPending: false,
      clerkVerificationPending: false,
    });
    try {
      await waitForPendingSessionEstablishments();
      if (operation !== authOperationGeneration) return;
      await trackSessionEstablishment(apiLogin(username, password));
      if (operation !== authOperationGeneration) return;
      useLocaleStore.getState().clearDemoLanguage();
      // Wipe the shared React Query cache on every login. The queryClient is
      // a single module-level instance for the whole browser tab, so without
      // this, a new session can still be served another user's cached herd
      // list / herd-scoped data (e.g. an admin's full herd list showing up
      // for a berater who logs in right after on the same browser).
      queryClient.clear();
      clearSnapshotPins();
      // Reset herd selection on every login so a stale URL herd_id from a
      // previous session (or a different user) never causes a 403 flash.
      useAppStore.getState().setHerd(null);
      // Never treat the login response as a confirmed dashboard session. /auth/me
      // is a read-only verification request and is the sole source of the user.
      const meData = await getConfirmedMe();
      if (operation !== authOperationGeneration) return;
      set((state) => ({
        user: meData,
        token: "cookie-session",
        isLoading: false,
        sessionChecked: true,
        sessionVersion: state.sessionVersion + 1,
      }));
    } catch (err: unknown) {
      if (operation !== authOperationGeneration) return;
      set({
        user: null,
        token: null,
        isLoading: false,
        error: authErrorMessage(err, "Login fehlgeschlagen"),
        sessionChecked: true,
      });
    }
  },

  loginDev: async (key: string) => {
    // Explicit workspace-only action; never an automatic Clerk fallback.
    if (!import.meta.env.DEV) return;
    const client = getClerkSessionClient();
    if (client && (!client.isLoaded || client.isSignedIn)) {
      set({ error: "Bitte zuerst die Alta-Konto-Anmeldung abschließen oder dich abmelden." });
      return;
    }
    const operation = ++authOperationGeneration;
    clearClerkAuthSuppressions();
    setExchangedClerkIdentity(null);
    resetSessionScopedData();
    set({
      user: null,
      token: null,
      isLoading: true,
      error: null,
      networkError: false,
      clerkIdentity: null,
      clerkRetryPending: false,
      clerkVerificationPending: false,
    });
    try {
      await waitForPendingSessionEstablishments();
      if (operation !== authOperationGeneration) return;
      await trackSessionEstablishment(apiDevLogin(key));
      if (operation !== authOperationGeneration) return;
      const user = await getConfirmedMe();
      if (operation !== authOperationGeneration) return;
      set((state) => ({
        user,
        token: "cookie-session",
        isLoading: false,
        sessionChecked: true,
        sessionVersion: state.sessionVersion + 1,
      }));
    } catch (err: unknown) {
      if (operation !== authOperationGeneration) return;
      set({
        user: null,
        token: null,
        isLoading: false,
        sessionChecked: true,
        networkError: err instanceof AuthMeVerificationError && isRetryableAuthMeError(err),
        error: authErrorMessage(err, "Dev-Login fehlgeschlagen"),
      });
    }
  },

  logout: async () => {
    // Attempt both providers even if either one fails. Keep a tab-local
    // suppression marker so a reload cannot silently recreate a session when
    // the Alta endpoint was temporarily unreachable.
    const operation = ++authOperationGeneration;
    markExplicitLogoutRequest();
    await waitForPendingSessionEstablishments();
    if (operation !== authOperationGeneration) return;
    const [altaResult, clerkResult] = await Promise.allSettled([
      trackSessionEstablishment(apiLogout()),
      signOutClerkSession(),
    ]);
    if (operation !== authOperationGeneration) return;
    resetSessionScopedData();
    setExchangedClerkIdentity(null);
    set((state) => ({
      user: null,
      token: null,
      sessionChecked: true,
      isLoading: false,
      error: null,
      clerkIdentity: null,
      clerkRetryPending: false,
      clerkVerificationPending: false,
      sessionVersion: state.sessionVersion + 1,
    }));
    if (altaResult.status === "rejected" || clerkResult.status === "rejected") {
      set({ error: "Die Abmeldung konnte nicht vollständig bestätigt werden. Bitte erneut versuchen." });
      if (altaResult.status === "rejected") throw altaResult.reason;
      if (clerkResult.status === "rejected") throw clerkResult.reason;
    }
  },

  refreshClerkIdentity: async (identity) => {
    if (!identity || (
      get().clerkIdentity === identity
      && !get().clerkRetryPending
      && !get().user?.must_change_password
    )) return;
    const operation = ++authOperationGeneration;
    clearClerkAuthSuppressions();
    setExchangedClerkIdentity(null);
    await waitForPendingSessionEstablishments();
    if (operation !== authOperationGeneration) return;
    const client = getClerkSessionClient();
    if (!client?.isLoaded || !client.isSignedIn || client.userId !== identity) return;
    // A changed Clerk identity must never continue seeing the previous Alta
    // account while the replacement cookie session is being established.
    resetSessionScopedData();
    set({
      user: null,
      token: null,
      isLoading: true,
      sessionChecked: false,
      error: null,
      clerkIdentity: identity,
      clerkRetryPending: false,
      clerkVerificationPending: false,
    });
    try {
      // Clear any cookie from a previous identity after all earlier
      // cookie-writing exchanges have drained, then mint the new session.
      await trackSessionEstablishment(apiLogout());
      if (operation !== authOperationGeneration) return;
      const user = await exchangeClerkIdentity(identity, operation);
      if (operation !== authOperationGeneration) return;
      const latestClient = getClerkSessionClient();
      if (latestClient?.userId !== identity) {
        if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
          await get().refreshClerkIdentity(latestClient.userId);
        }
        return;
      }
      setExchangedClerkIdentity(identity);
      resetSessionScopedData();
      set((state) => ({
        user,
        token: "cookie-session",
        isLoading: false,
        sessionChecked: true,
        clerkIdentity: identity,
        clerkRetryPending: false,
        clerkVerificationPending: false,
        sessionVersion: state.sessionVersion + 1,
      }));
    } catch (err: unknown) {
      if (operation !== authOperationGeneration) return;
      const latestClient = getClerkSessionClient();
      if (err instanceof SupersededAuthOperation) {
        if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
          await get().refreshClerkIdentity(latestClient.userId);
        } else {
          set({
            user: null,
            token: null,
            isLoading: false,
            sessionChecked: true,
            clerkIdentity: null,
            clerkRetryPending: false,
            clerkVerificationPending: false,
          });
        }
        return;
      }
      if (latestClient?.userId !== identity) {
        if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
          await get().refreshClerkIdentity(latestClient.userId);
        }
        return;
      }
      if (isRetryableAuthTransportError(err)) {
        resetSessionScopedData();
        set((state) => ({
          user: null,
          token: null,
          isLoading: false,
          sessionChecked: true,
          clerkIdentity: identity,
          clerkRetryPending: true,
          clerkVerificationPending: err instanceof AuthMeVerificationError,
          error: "Die Clerk-Anmeldung ist vorübergehend nicht erreichbar. Bitte versuche es erneut.",
          sessionVersion: state.sessionVersion + 1,
        }));
        return;
      }
      markClerkExchangeFailed();
      markExplicitLogoutRequest();
      setExchangedClerkIdentity(null);
      await Promise.allSettled([
        trackSessionEstablishment(apiLogout()),
        getClerkSessionClient()?.userId === identity
          ? getClerkSessionClient()?.signOut() ?? Promise.resolve()
          : Promise.resolve(),
      ]);
      const clientAfterCleanup = getClerkSessionClient();
      const newerSignedInIdentity = clientAfterCleanup?.isLoaded
        && clientAfterCleanup.isSignedIn
        && Boolean(clientAfterCleanup.userId)
        && clientAfterCleanup.userId !== identity;
      if (operation !== authOperationGeneration || newerSignedInIdentity) return;
      resetSessionScopedData();
      set((state) => ({
        user: null,
        token: null,
        isLoading: false,
        sessionChecked: true,
        clerkIdentity: null,
        clerkRetryPending: false,
        clerkVerificationPending: false,
        error: authErrorMessage(
          err,
          "Dein Alta-Konto ist noch keinem DairyInsight-Zugang zugeordnet. Bitte wende dich an deinen Alta-Ansprechpartner.",
        ),
        sessionVersion: state.sessionVersion + 1,
      }));
    }
  },

  retryClerkLogin: async () => {
    const identity = get().clerkIdentity;
    const client = getClerkSessionClient();
    if (identity && client?.isLoaded && client.isSignedIn && client.userId === identity) {
      if (get().clerkVerificationPending) {
        const operation = ++authOperationGeneration;
        set({ isLoading: true, error: null });
        await waitForPendingSessionEstablishments();
        if (operation !== authOperationGeneration) return;
        try {
          const user = await getConfirmedMe();
          if (operation !== authOperationGeneration) return;
          const latestClient = getClerkSessionClient();
          if (
            !latestClient?.isLoaded
            || !latestClient.isSignedIn
            || latestClient.userId !== identity
          ) {
            if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
              await get().refreshClerkIdentity(latestClient.userId);
            } else {
              set({
                user: null,
                token: null,
                isLoading: false,
                sessionChecked: true,
                clerkIdentity: null,
                clerkRetryPending: false,
                clerkVerificationPending: false,
              });
            }
            return;
          }
          setExchangedClerkIdentity(identity);
          resetSessionScopedData();
          set((state) => ({
            user,
            token: "cookie-session",
            isLoading: false,
            sessionChecked: true,
            clerkIdentity: identity,
            clerkRetryPending: false,
            clerkVerificationPending: false,
            error: null,
            sessionVersion: state.sessionVersion + 1,
          }));
        } catch (err: unknown) {
          if (operation !== authOperationGeneration) return;
          set({
            user: null,
            token: null,
            isLoading: false,
            sessionChecked: true,
            clerkRetryPending: true,
            clerkVerificationPending: true,
            error: authErrorMessage(
              err,
              "Die Clerk-Anmeldung ist vorübergehend nicht erreichbar. Bitte versuche es erneut.",
            ),
          });
        }
        return;
      }
      await get().refreshClerkIdentity(identity);
    } else {
      await get().bootstrap();
    }
  },

  impersonate: async (userId: number) => {
    await impersonateUser(userId);
    // Same hygiene as login: never leak cached data between identities.
    queryClient.clear();
    clearSnapshotPins();
    useAppStore.getState().setHerd(null);
    set({ user: null, token: null, isLoading: true, error: null });
    try {
      const meData = await getConfirmedMe();
      set((state) => ({
        user: meData,
        token: "cookie-session",
        isLoading: false,
        sessionChecked: true,
        error: null,
        sessionVersion: state.sessionVersion + 1,
      }));
    } catch (err: unknown) {
      set((state) => ({
        user: null,
        token: null,
        isLoading: false,
        sessionChecked: true,
        error: authErrorMessage(err, "Die Sitzung konnte nicht überprüft werden."),
        sessionVersion: state.sessionVersion + 1,
      }));
    }
  },

  stopImpersonation: async () => {
    await apiStopImpersonation();
    queryClient.clear();
    clearSnapshotPins();
    useAppStore.getState().setHerd(null);
    set({ user: null, token: null, isLoading: true, error: null });
    try {
      // Server-side validation of the restored token via /auth/me.
      const meData = await getConfirmedMe();
      queryClient.clear();
      clearSnapshotPins();
      useAppStore.getState().setHerd(null);
      set((state) => ({
        user: meData,
        token: "cookie-session",
        isLoading: false,
        sessionChecked: true,
        error: null,
        sessionVersion: state.sessionVersion + 1,
      }));
    } catch (err: unknown) {
      queryClient.clear();
      clearSnapshotPins();
      useAppStore.getState().setHerd(null);
      set((state) => ({
        user: null,
        token: null,
        isLoading: false,
        sessionChecked: true,
        error: authErrorMessage(err, "Die Sitzung konnte nicht wiederhergestellt werden."),
        sessionVersion: state.sessionVersion + 1,
      }));
    }
  },

  bootstrap: () => {
    // Deduplicate simultaneous router/provider effects and StrictMode's
    // development-only repeated mount.
    if (bootstrapInFlight) return bootstrapInFlight;
    if (get().isLoading && !get().sessionChecked) return Promise.resolve();

    const operation = authOperationGeneration;
    const run = async () => {
      if (hasExplicitLogoutRequest()) {
        resetSessionScopedData();
        set({
          user: null,
          token: null,
          isLoading: false,
          sessionChecked: true,
          clerkIdentity: null,
          clerkRetryPending: false,
          clerkVerificationPending: false,
        });
        return;
      }
      set({ isLoading: true, networkError: false });
      try {
        // Ordinary Alta cookie sessions remain the first choice. A password-
        // restricted session must not hide an available verified Portal login.
        const user = await getConfirmedMe();
        if (operation !== authOperationGeneration) return;
        const client = getClerkSessionClient();
        const linkedIdentity = get().clerkIdentity || sessionStorage.getItem("alta_clerk_identity");
        if (
          client?.isLoaded
          && client.isSignedIn
          && client.userId
          && linkedIdentity
          && linkedIdentity !== client.userId
        ) {
          await get().refreshClerkIdentity(client.userId);
          return;
        }
        if (
          user.must_change_password
          && isClerkEnabled()
          && client?.isLoaded
          && client.isSignedIn
          && client.userId
          && !hasExplicitLogoutRequest()
          && !hasClerkExchangeFailed()
        ) {
          // Use the existing cookie ordering, server-side identity mapping and
          // /auth/me confirmation. Never clear the password flag locally.
          await get().refreshClerkIdentity(client.userId);
          return;
        }
        // A verified cookie-session reload is not a change of account.
        // HerdSelector validates the restored URL selection against /herds.
        resetSessionScopedData(user.role !== "demo", true);
        set((state) => ({
          user,
          token: "cookie-session",
          isLoading: false,
          sessionChecked: true,
          error: null,
          sessionVersion: state.sessionVersion + 1,
        }));
      } catch (err: unknown) {
        if (operation !== authOperationGeneration) return;
        const source = authErrorSource(err) as { response?: { status?: number } } | null;
        const status = source?.response?.status;
        const client = getClerkSessionClient();
        const canExchange = isClerkEnabled()
          && status === 401
          && client?.isLoaded
          && client.isSignedIn
          && Boolean(client.userId)
          && !hasClerkExchangeFailed();
        if (canExchange && client?.userId) {
          try {
            const user = await exchangeClerkIdentity(client.userId, operation);
            if (operation !== authOperationGeneration) return;
            const latestClient = getClerkSessionClient();
            if (latestClient?.userId !== client.userId) {
              if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
                await get().refreshClerkIdentity(latestClient.userId);
              }
              return;
            }
            setExchangedClerkIdentity(client.userId);
            resetSessionScopedData();
            set((state) => ({
              user,
              token: "cookie-session",
              isLoading: false,
              sessionChecked: true,
              clerkIdentity: client.userId,
              clerkRetryPending: false,
              clerkVerificationPending: false,
              error: null,
              sessionVersion: state.sessionVersion + 1,
            }));
            return;
          } catch (exchangeError: unknown) {
            if (operation !== authOperationGeneration) return;
            const latestClient = getClerkSessionClient();
            if (exchangeError instanceof SupersededAuthOperation) {
              if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
                await get().refreshClerkIdentity(latestClient.userId);
              } else {
                set({
                  user: null,
                  token: null,
                  isLoading: false,
                  sessionChecked: true,
                  clerkIdentity: null,
                  clerkRetryPending: false,
                  clerkVerificationPending: false,
                });
              }
              return;
            }
            if (latestClient?.userId !== client.userId) {
              if (latestClient?.isLoaded && latestClient.isSignedIn && latestClient.userId) {
                await get().refreshClerkIdentity(latestClient.userId);
              }
              return;
            }
            if (isRetryableAuthTransportError(exchangeError)) {
              set({
                isLoading: false,
                sessionChecked: true,
                clerkIdentity: client.userId,
                clerkRetryPending: true,
                clerkVerificationPending: exchangeError instanceof AuthMeVerificationError,
                error: "Die Clerk-Anmeldung ist vorübergehend nicht erreichbar. Bitte versuche es erneut.",
              });
              return;
            }
            // Rejected/unmapped identities must not be silently retried on
            // each StrictMode pass, nor left with a reusable Alta cookie.
            markClerkExchangeFailed();
            markExplicitLogoutRequest();
            setExchangedClerkIdentity(null);
            await Promise.allSettled([
              trackSessionEstablishment(apiLogout()),
              getClerkSessionClient()?.userId === client.userId
                ? getClerkSessionClient()?.signOut() ?? Promise.resolve()
                : Promise.resolve(),
            ]);
            const clientAfterCleanup = getClerkSessionClient();
            const newerSignedInIdentity = clientAfterCleanup?.isLoaded
              && clientAfterCleanup.isSignedIn
              && Boolean(clientAfterCleanup.userId)
              && clientAfterCleanup.userId !== client.userId;
            if (operation !== authOperationGeneration || newerSignedInIdentity) return;
            resetSessionScopedData();
            set((state) => ({
              user: null,
              token: null,
              isLoading: false,
              sessionChecked: true,
              clerkIdentity: null,
              clerkRetryPending: false,
              clerkVerificationPending: false,
              error: authErrorMessage(
                exchangeError,
                "Dein Alta-Konto ist noch keinem DairyInsight-Zugang zugeordnet. Bitte wende dich an deinen Alta-Ansprechpartner.",
              ),
              sessionVersion: state.sessionVersion + 1,
            }));
            return;
          }
        }

        // Never create a development session during bootstrap. Dev entry
        // requires an explicit click and the server-verified secret.
        if (!(source as { response?: unknown } | null)?.response) {
          set((state) => ({
            user: null,
            token: null,
            isLoading: false,
            networkError: true,
            sessionChecked: true,
            error: authErrorMessage(err, "Server vorübergehend nicht erreichbar. Bitte kurz warten und erneut versuchen."),
            sessionVersion: state.sessionVersion + 1,
          }));
        } else {
          resetSessionScopedData();
          set((state) => ({
            user: null,
            token: null,
            isLoading: false,
            sessionChecked: true,
            clerkIdentity: null,
            clerkRetryPending: false,
            clerkVerificationPending: false,
            networkError: isRetryableAuthMeError(err),
            error: status === 401
              ? null
              : authErrorMessage(err, "Die Sitzung konnte nicht überprüft werden. Bitte erneut versuchen."),
            sessionVersion: state.sessionVersion + 1,
          }));
        }
      }
    };
    bootstrapInFlight = run().finally(() => {
      bootstrapInFlight = null;
    });
    return bootstrapInFlight;
  },

  clearNetworkError: () => set({ networkError: false }),

  clearMustChangePassword: () => {
    set((state) => ({
      user: state.user ? { ...state.user, must_change_password: false } : null,
    }));
  },
}));
